/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { XdpV1_Org_Coursera_Degree_Stored_Api_UpsellType as UpsellTypes } from '__generated__/graphql-types';

import { Typography2 } from '@coursera/cds-core';

import type { StackabilityModuleFragment } from 'bundles/degree-description/__generated__/contentfulTypes';
import LazyCdsIcon from 'bundles/degree-description/components/LazyCdsIcon';
import UpsellCard from 'bundles/degree-description/components/modules/upsells/UpsellCard';
import Container from 'bundles/degree-description/components/shared/Container';
import StackabilityPanel from 'bundles/degree-description/components/shared/StackabilityPanel';
import { useDegreeDescriptionUpsellsData } from 'bundles/degree-description/contexts/DegreeDescriptionPageUpsellsContext';
import { useDegreeDescriptionEventingContext } from 'bundles/degree-description/providers/DegreeDescriptionEventingProvider';
import {
  filterStackableItemsByUpsellType,
  getProductVariantString,
  getUpsellInfo,
} from 'bundles/degree-description/utils/utils';

import _t from 'i18n!nls/degree-description';

import { getStackabilityDummyContent } from './dummyContent';

const styles = {
  module: css`
    padding: var(--cds-spacing-400) 0;
  `,
  heading: css`
    margin: 0 0 var(--cds-spacing-200);
  `,
  description: css`
    margin: 0;
  `,
};

type Props = {
  content?: StackabilityModuleFragment | null;
};

/** Uses Contentful copy where present, falling back to the default copy per field. */
const getModuleContent = (content: Props['content']) => {
  const fallback = getStackabilityDummyContent();
  const contentfulValueProps = (content?.valuePropsCollection?.items ?? []).flatMap((item) =>
    item?.heading
      ? [
          {
            icon: item.cdsIcon ? <LazyCdsIcon name={item.cdsIcon} size="medium" /> : null,
            headline: item.heading,
            description: item.description ?? '',
          },
        ]
      : []
  );

  return {
    heading: content?.heading || fallback.heading,
    description: content?.description || fallback.description,
    valueProps: contentfulValueProps.length > 0 ? contentfulValueProps : fallback.valueProps,
  };
};

const StackabilityModule = ({ content }: Props): React.ReactElement | null => {
  const { stackableItemsWithUpsellType } = useDegreeDescriptionUpsellsData();
  const { useGetViewPageSectionRef } = useDegreeDescriptionEventingContext();
  const pageSectionRef = useGetViewPageSectionRef('upsell');
  const products = React.useMemo(() => {
    const items = stackableItemsWithUpsellType ?? [];
    return [UpsellTypes.OffersCredit, UpsellTypes.PartOf]
      .flatMap((upsellType) => filterStackableItemsByUpsellType(items, upsellType) ?? [])
      .flatMap((item) => {
        const upsellInfo = getUpsellInfo([item]);
        return upsellInfo?.upsell ? [{ item, upsellInfo, product: upsellInfo.upsell }] : [];
      });
  }, [stackableItemsWithUpsellType]);

  if (products.length === 0) {
    return null;
  }

  const { heading, description, valueProps } = getModuleContent(content);

  return (
    <div ref={pageSectionRef} css={styles.module}>
      <Container>
        <StackabilityPanel
          heading={
            <Typography2 variant="titleXSmall" component="h2" css={styles.heading}>
              {heading}
            </Typography2>
          }
          description={
            <Typography2 variant="bodyPrimary" color="supportText" component="p" css={styles.description}>
              {description}
            </Typography2>
          }
          valueProps={valueProps.map(({ icon, headline, description: valuePropDescription }) => ({
            icon,
            heading: headline,
            description: valuePropDescription,
          }))}
          carouselLabel={_t('Degree coursework')}
          cards={products.map(({ item, upsellInfo, product }, index) => ({
            key: `${item.upsellType}-${product.id}`,
            label: product.name || undefined,
            node: (
              <UpsellCard
                productId={product.id}
                title={product.name || ''}
                bannerImageUrl={upsellInfo.upsellHeaderImage}
                slug={product.slug}
                productVariant={product.productVariant || ''}
                partners={upsellInfo.upsellPartners}
                productUrl={upsellInfo.url}
                tagline={upsellInfo.tagline}
                footerText={[getProductVariantString(product.productVariant)].filter(Boolean)}
                loading={false}
                trackingNamePrefix="redesign_stackability"
                trackingSectionName="upsell"
                index={index}
                isFixedHeight={false}
              />
            ),
          }))}
        />
      </Container>
    </div>
  );
};

export default StackabilityModule;
